import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Tag, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    document.title = 'Blog | Electrical Tips & News | Island Coastal Electric';
    const setMeta = (name, content, property) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector('meta[' + attr + '="' + name + '"]');
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Expert electrical tips, project insights, and BC code updates from Island Coastal Electric. Your trusted Vancouver Island electrician blog.');
    setMeta('og:title', 'Blog | Island Coastal Electric', true);
    setMeta('og:description', 'Expert electrical tips, project insights, and BC code updates from Island Coastal Electric.', true);
  }, []);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const res = await axios.get(API_BASE + '/api/blog/posts');
        setPosts(res.data);
      } catch (err) {
        console.error('Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    };
    loadPosts();
  }, []);

  const categories = ['all', ...new Set(posts.map(p => p.category))];

  const filteredPosts = posts.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.excerpt && p.excerpt.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCat = selectedCategory === 'all' || p.category === selectedCategory;
    return matchesSearch && matchesCat;
  });

  return (
    <div className="min-h-screen bg-primary-dark pt-28 lg:pt-32" data-testid="blog-page">
      {/* Hero */}
      <section className="py-16 md:py-20 bg-secondary-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="text-electric-teal font-bold uppercase tracking-wider text-sm">Blog</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mt-2 mb-4">
              Electrical Tips & Insights
            </h1>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Expert advice on electrical safety, home upgrades, and BC code requirements from Vancouver Island's trusted electricians.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-secondary-dark border border-white/10 rounded-lg text-white placeholder-white/40 focus:border-electric-teal focus:outline-none"
                data-testid="blog-search-public"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={'px-4 py-2 rounded-full text-sm font-medium transition-all ' +
                    (selectedCategory === cat
                      ? 'bg-electric-teal text-primary-dark'
                      : 'bg-secondary-dark text-white/60 border border-white/10 hover:border-electric-teal/50')}
                  data-testid={'blog-cat-' + cat}
                >
                  {cat === 'all' ? 'All Posts' : cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-white/50 text-center py-12">Loading articles...</div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-white/50 text-lg mb-2">No articles found</p>
              <p className="text-white/30">Check back soon for new content!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  data-testid={'blog-card-' + post.slug}
                >
                  <Link to={'/blog/' + post.slug} className="group block bg-secondary-dark rounded-lg border border-white/10 hover:border-electric-teal/50 transition-all overflow-hidden">
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-electric-teal text-xs font-semibold uppercase tracking-wider">{post.category}</span>
                        <span className="text-white/30 text-xs flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(post.published_at || post.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      <h2 className="text-white font-bold text-lg mb-3 group-hover:text-electric-teal transition-colors leading-tight">
                        {post.title}
                      </h2>
                      <p className="text-white/50 text-sm line-clamp-3 mb-4">
                        {post.excerpt}
                      </p>
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {post.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="text-xs px-2 py-0.5 bg-white/5 text-white/40 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <span className="text-electric-teal text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read More <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-secondary-dark">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Need Electrical Help?</h2>
          <p className="text-white/60 mb-8">Our licensed electricians are ready to help with your project.</p>
          <Button asChild size="lg" className="bg-electric-teal text-primary-dark font-bold">
            <Link to="/book-estimate">Get a Free Estimate <ArrowRight className="ml-2 w-5 h-5" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
